import { usePaginatedQuery } from '@hooks/usePaginatedQuery';
import { SEARCH_TALENT_FILTERS, SEARCH_TALENTS } from '@gql/talentSearch';
import { useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { usePageSize } from '@hooks/usePageSize';
import {
  defaultAvailableFilters,
  ITalentSearchAvailableFilters,
  defaultSearchFilters,
  ITalentSearchFilter,
  SearchContext,
} from '../types';

interface UseTalentSearchOptions {
  context: SearchContext;
  jobOfferId?: string;
  initialFilters?: Partial<ITalentSearchFilter>;
}

export function useTalentSearch({ context, jobOfferId, initialFilters }: UseTalentSearchOptions) {
  const [filters, setFilters] = useState<ITalentSearchFilter>({
    ...defaultSearchFilters,
    ...initialFilters,
  });
  const [page, setPage] = useState(0);
  const { pageSize, storePageSize } = usePageSize({
    storageKey: `talent-search-${context}-page-size`,
  });
  const [sortBy, setSortBy] = useState<{ field: string; order: string }>({
    field: 'CREATED_AT',
    order: 'DESC',
  });

  // Normalize filters to GraphQL input format
  const searchInput = useMemo(() => {
    const input: Record<string, any> = {
      context,
      sortBy: sortBy.field,
      sortOrder: sortBy.order,
    };

    if (jobOfferId) input.jobOfferId = jobOfferId;
    if (jobOfferId && filters.jobOfferScoringStates?.length) {
      input.jobOfferScoringStates = filters.jobOfferScoringStates;
    }
    if (jobOfferId && filters.resumeReadinessStatuses?.length) {
      input.resumeReadinessStatuses = filters.resumeReadinessStatuses;
    }
    if (jobOfferId && (filters.jobOfferScoringRange?.min != null || filters.jobOfferScoringRange?.max != null)) {
      input.jobOfferScoringRange = {
        min: filters.jobOfferScoringRange?.min ?? null,
        max: filters.jobOfferScoringRange?.max ?? null,
      };
    }

    // Only include non-empty filter values
    if (filters.query) input.query = filters.query;
    if (filters.professionIds?.length) input.professionIds = filters.professionIds;
    if (filters.specialtyIds?.length) input.specialtyIds = filters.specialtyIds;
    if (filters.softSkillIds?.length) input.softSkillIds = filters.softSkillIds;
    if (filters.certificateIds?.length) input.certificateIds = filters.certificateIds;
    if (filters.diplomaTypes?.length) input.diplomaTypes = filters.diplomaTypes;

    if (filters.experienceRange?.min != null || filters.experienceRange?.max != null) {
      input.experienceRange = {
        min: filters.experienceRange?.min ?? null,
        max: filters.experienceRange?.max ?? null,
      };
    }

    if (filters.expectedSalaryRange?.min != null || filters.expectedSalaryRange?.max != null) {
      input.expectedSalaryRange = {
        min: filters.expectedSalaryRange?.min ?? null,
        max: filters.expectedSalaryRange?.max ?? null,
      };
    }

    if (filters.geoLocation && 'lat' in filters.geoLocation && filters.geoLocation.lat != null) {
      input.geoLocation = {
        lat: filters.geoLocation.lat,
        lng: filters.geoLocation.lng,
        radiusKm: (filters.geoLocation as any).radius || 30,
      };
    }

    if (filters.cities?.length) input.cities = filters.cities;
    if (filters.countries?.length) input.countries = filters.countries;
    if (filters.availability?.length) input.availability = filters.availability;
    if (filters.currentStatus?.length) input.currentStatus = filters.currentStatus;
    if (filters.contractTypes?.length) input.contractTypes = filters.contractTypes;
    if (filters.languages?.length) input.languages = filters.languages;

    // Favorites-only fields
    if (context === 'FAVORITES') {
      if (filters.minRating != null && filters.minRating > 0) input.minRating = filters.minRating;
      if (filters.tags?.length) input.tags = filters.tags;
      if (filters.priority?.length) input.priority = filters.priority;
    }

    return input;
  }, [context, jobOfferId, filters, sortBy]);

  const { data: availableFiltersData, loading: filtersLoading } = useQuery(
    SEARCH_TALENT_FILTERS,
    {
      variables: { input: searchInput },
      fetchPolicy: 'cache-and-network',
    },
  );

  const availableFilters: ITalentSearchAvailableFilters =
    availableFiltersData?.searchTalentFilters || defaultAvailableFilters;

  const {
    rows: talents,
    totalDocs,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
    refetch,
  } = usePaginatedQuery({
    query: SEARCH_TALENTS,
    variables: { input: searchInput },
    page,
    pageSize,
    paginationMode: 'offset',
    extractResult: (data: any) => {
      const meta = data?.searchTalents?.meta;
      return {
        docs: data?.searchTalents?.docs || [],
        totalDocs: meta?.totalDocs || 0,
        meta: {
          page: meta?.page || 1,
          totalPages: meta?.totalPages || 0,
          totalDocs: meta?.totalDocs || 0,
          nextPage: meta?.nextPage ?? null,
          prevPage: meta?.prevPage ?? null,
        },
      };
    },
  });

  const updateFilter = useCallback((key: keyof ITalentSearchFilter, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  const setAllFilters = useCallback((newFilters: ITalentSearchFilter) => {
    setFilters(newFilters);
    setPage(0);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultSearchFilters);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage - 1);
  }, []);

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      storePageSize(newSize);
      setPage(0);
    },
    [storePageSize],
  );

  return {
    filters,
    setFilters: setAllFilters,
    updateFilter,
    resetFilters,
    sortBy,
    setSortBy,
    talents,
    totalDocs,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
    refetch,
    availableFilters,
    filtersLoading,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
  };
}
